import {
	Autocomplete,
	Box,
	Button,
	Paper,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckIcon from '@mui/icons-material/Check';
import React from 'react';
import { stateProvinceList } from '../../data/stateProvinceList';
import { normalizeLicensePlate } from '../../utils/normalizeLicensePlate';

interface ScanPreviewProps {
	capturedImage: string;
	licensePlateNumber: string;
	setLicensePlateNumber: (val: string) => void;
	selectedState: string | null;
	setSelectedState: (val: string) => void;
	onConfirm: () => void;
	onRetry: () => void;
	canConfirm: boolean;
	helperText?: string;
}

const ScanPreview: React.FC<ScanPreviewProps> = ({
	capturedImage,
	licensePlateNumber,
	setLicensePlateNumber,
	selectedState,
	setSelectedState,
	onConfirm,
	onRetry,
	canConfirm,
	helperText,
}) => {
	return (
		<Box
			sx={{
				flex: 1,
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
			}}
		>
			<Box
				sx={{
					px: 2,
					pt: 1,
					pb: 1,
					display: 'flex',
					flexDirection: 'column',
					gap: 1.5,
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
				}}
			>
				<Typography variant="subtitle2" color="text.secondary">
					Review the scan
				</Typography>
				<Paper
					variant="outlined"
					sx={{
						borderRadius: 2,
						overflow: 'hidden',
						bgcolor: 'common.black',
						display: 'flex',
						justifyContent: 'center',
					}}
				>
					<Box
						component="img"
						src={capturedImage}
						alt="Captured license plate"
						sx={{
							width: '100%',
							maxHeight: 220,
							objectFit: 'contain',
							display: 'block',
						}}
					/>
				</Paper>

				<Typography variant="caption" color="text.secondary">
					Edit if the extracted text is wrong.
				</Typography>

				<Stack direction="row" spacing={1} alignItems="flex-start">
					<TextField
						label="Plate"
						value={licensePlateNumber}
						onChange={(e) =>
							setLicensePlateNumber(
								normalizeLicensePlate(e.target.value),
							)
						}
						autoComplete="off"
						sx={{ flexGrow: 1 }}
						inputProps={{
							autoCapitalize: 'characters',
							autoCorrect: 'off',
							spellCheck: false,
						}}
					/>
					<Autocomplete
						options={stateProvinceList}
						getOptionLabel={(o) => o.code}
						renderOption={(optProps, option) => {
							const { key, ...rest } = optProps as React.HTMLAttributes<HTMLLIElement> & {
								key?: React.Key;
							};
							return (
								<li key={option.code} {...rest}>
									{option.label}
								</li>
							);
						}}
						value={
							stateProvinceList.find(
								(s) => s.code === selectedState,
							) || null
						}
						onChange={(_, newValue) =>
							setSelectedState(newValue ? newValue.code : '')
						}
						renderInput={(params) => (
							<TextField {...params} label="State" />
						)}
						sx={{ width: 110 }}
					/>
				</Stack>

				{helperText && (
					<Typography variant="caption" color="text.secondary">
						{helperText}
					</Typography>
				)}
			</Box>

			<Box
				sx={{
					px: 2,
					pt: 1,
					pb: 'calc(16px + env(safe-area-inset-bottom))',
					display: 'flex',
					gap: 1.5,
					flexShrink: 0,
				}}
			>
				<Button
					variant="outlined"
					color="secondary"
					size="large"
					startIcon={<ReplayIcon />}
					onClick={onRetry}
					sx={{
						borderRadius: 2,
						textTransform: 'none',
						fontWeight: 500,
					}}
				>
					Retry
				</Button>
				<Button
					variant="contained"
					color="primary"
					size="large"
					fullWidth
					data-tour="scan-preview-confirm"
					startIcon={<CheckIcon />}
					onClick={onConfirm}
					disabled={!canConfirm}
					sx={{
						py: 1.5,
						borderRadius: 3,
						fontSize: '1rem',
						fontWeight: 500,
						textTransform: 'none',
						boxShadow: 3,
					}}
				>
					Confirm &amp; continue
				</Button>
			</Box>
		</Box>
	);
};

export default ScanPreview;
